// src/pages/ui.ts —— 原型样式原语的原生移植（原型 L635–640 chip / cell / act / btn / field / li，L685 统计卡，L676 表头，L847 页码，L872 抽屉页签），样式串逐字
import { ApiError } from '../../../shared/src/api/client';
import type { RouteKey } from '../../../shared/src/api/routes';

export type ChipKind = 'green' | 'gray' | 'amber' | 'red' | 'blue' | 'purple';

const CHIP: Readonly<Record<ChipKind, readonly [string, string]>> = {
  green: ['#e3f6ee', '#0d9a68'], gray: ['#eef3f0', '#6b7772'], amber: ['#fff3df', '#c47a0c'],
  red: ['#fdecec', '#d1434b'], blue: ['#e7f3fb', '#1f6fa8'], purple: ['#ece9fb', '#6b4fd8'],
};

export interface CellVals {
  v: string;
  sub: string;
  isPlain: boolean;
  isStatus: boolean;
  chip?: string;
  style: string;
  badge?: { text: string; style: string; title: string }; // 主值右侧附加章（C-13「毛利倒挂」）
  subDanger?: boolean; // 副文本红字（06 C-08 / C-12：approved 超 SLA 时状态章下方红字「超时 {h}h」，D26）
}

const SUB_STYLE = 'font-size:11px;color:#8a958f;margin-top:2px'; // 原型表格行副文本样式串逐字（0107 表格行）
const SUB_DANGER_STYLE = 'font-size:11px;color:#d1434b;margin-top:2px';

// 模板副文本样式（0107 S-38 偏差块经 c.subStyle 读取）
export function subStyleOf(c: CellVals): string {
  return c.subDanger ? SUB_DANGER_STYLE : SUB_STYLE;
}

export function withBadge(c: CellVals, kind: ChipKind, text: string, title: string): CellVals {
  const k = CHIP[kind];
  return { ...c, badge: { text, title, style: `display:inline-block;margin-left:6px;padding:3px 9px;border-radius:999px;font-size:11px;font-weight:700;background:${k[0]};color:${k[1]};white-space:nowrap;font-family:'Plus Jakarta Sans',sans-serif` } };
}

export function chip(kind: ChipKind, label: string): CellVals {
  const c = CHIP[kind];
  return { v: label, sub: '', isStatus: true, isPlain: false, chip: `display:inline-block;padding:3px 9px;border-radius:999px;font-size:11px;font-weight:700;background:${c[0]};color:${c[1]};white-space:nowrap`, style: 'padding:12px 16px;vertical-align:middle' };
}

export function cell(v: string | number, sub?: string, mono?: boolean): CellVals {
  return { v: String(v), sub: sub ?? '', isPlain: true, isStatus: false, style: `padding:12px 16px;vertical-align:middle;${mono ? "font-family:'JetBrains Mono',monospace;font-size:12px;" : ''}white-space:nowrap` };
}

export interface ActVals {
  label: string;
  title: string;
  run: (e: { stopPropagation(): void }) => void;
  style: string;
}

export function act(label: string, run: (() => void) | null, danger = false, gate?: { disabled: boolean; title: string }): ActVals {
  const base = `height:28px;padding:0 10px;margin-left:6px;border:1px solid ${danger ? '#f3c2c5' : '#e6ebe8'};border-radius:7px;background:#fff;color:${danger ? '#d1434b' : '#0f1a17'};font-size:12px;font-weight:700;cursor:pointer`;
  return {
    label,
    title: gate?.title ?? '',
    run: (e) => {
      e.stopPropagation();
      if (!gate?.disabled && run) run();
    },
    style: gate?.disabled ? `${base};opacity:.5;cursor:not-allowed` : base,
  };
}

export type BtnKind = 'primary' | 'danger' | 'green' | '';

export interface BtnVals {
  label: string;
  title: string;
  run: () => void;
  style: string;
}

export function btn(label: string, run: () => void, kind: BtnKind = '', gate?: { disabled: boolean; title: string }): BtnVals {
  const bg = kind === 'primary' ? '#0f1a17' : kind === 'danger' ? '#d1434b' : kind === 'green' ? '#0d9a68' : '#fff';
  const base = `height:38px;padding:0 16px;border:1px solid ${kind ? 'transparent' : '#e6ebe8'};border-radius:10px;background:${bg};color:${kind ? '#fff' : '#0f1a17'};font-weight:700;font-size:13px;cursor:pointer`;
  return { label, title: gate?.title ?? '', run: () => (gate?.disabled ? undefined : run()), style: gate?.disabled ? `${base};opacity:.5;cursor:not-allowed` : base };
}

export function field(k: string, v: string | number, wide = false): { k: string; v: string; style: string } {
  return { k, v: String(v), style: `background:#f8faf9;border-radius:10px;padding:10px 12px;${wide ? 'grid-column:1/-1' : ''}` };
}

export type Tone = 'good' | 'bad' | 'warn' | '';

export interface LiVals {
  left: string;
  sub: string;
  right: string;
  rightSub: string;
  go: () => void;
  rowStyle: string;
  rightStyle: string;
}

export function li(left: string, sub: string, right: string, rightSub: string, tone: Tone, go?: () => void): LiVals {
  return {
    left,
    sub,
    right,
    rightSub,
    go: go ?? (() => undefined),
    rowStyle: `display:flex;gap:12px;align-items:center;padding:11px 0;border-top:1px solid #f0f3f1;${go ? 'cursor:pointer' : ''}`,
    rightStyle: `font-size:11px;margin-top:2px;color:${tone === 'bad' ? '#d1434b' : tone === 'warn' ? '#c47a0c' : tone === 'good' ? '#0d9a68' : '#8a958f'}`,
  };
}

export interface StatCardVals {
  label: string;
  value: string;
  sub: string;
  style: string;
  valueStyle: string;
}

export function statCard(label: string, value: string, sub: string, dark = false, danger = false): StatCardVals {
  return {
    label,
    value,
    sub,
    style: `border-radius:16px;padding:16px 18px;border:1px solid ${dark ? 'transparent' : '#e6ebe8'};background:${dark ? '#0f1a17' : '#fff'};color:${dark ? '#fff' : '#0f1a17'}`,
    // 原型统计卡数值样式串逐字（0107 统计卡行）；danger 追加红字（06 C-27 覆盖率 < 100%）
    valueStyle: `font-size:24px;font-weight:800;margin-top:6px${danger ? ';color:#d1434b' : ''}`,
  };
}

export function thStyle(active: boolean, sortable: boolean): string {
  return `padding:11px 16px;text-align:left;font-size:11px;color:${active ? '#0d9a68' : '#6b7772'};font-weight:700;letter-spacing:.3px;text-transform:uppercase;white-space:nowrap;${sortable ? 'cursor:pointer;user-select:none' : ''}`;
}

export function tabStyle(active: boolean): string {
  return `height:34px;padding:0 14px;border:1px solid ${active ? '#0f1a17' : '#e6ebe8'};border-radius:999px;background:${active ? '#0f1a17' : '#fff'};color:${active ? '#fff' : '#6b7772'};font-size:13px;font-weight:700;cursor:pointer`;
}

export function pageBtnStyle(cur: boolean): string {
  return `height:28px;min-width:30px;padding:0 8px;border:${cur ? '0' : '1px solid #e6ebe8'};border-radius:7px;background:${cur ? '#0f1a17' : '#fff'};color:${cur ? '#fff' : '#0f1a17'};font-size:12px;font-weight:${cur ? 800 : 600};cursor:pointer`;
}

export function dtabStyle(active: boolean): string {
  return `height:34px;padding:0 12px;border:0;border-bottom:2px solid ${active ? '#0d9a68' : 'transparent'};background:transparent;color:${active ? '#0f1a17' : '#8a958f'};font-size:13px;font-weight:700;cursor:pointer;white-space:nowrap`;
}

// 06 全局约定 X-34：未映射状态值 → gray 章 + 原值，前端告警一次
const warned = new Set<string>();
export function chipOf(map: Readonly<Record<string, readonly [ChipKind, string]>>, value: string, column: string): CellVals {
  const hit = map[value];
  if (hit) return chip(hit[0], hit[1]);
  const k = `${column}:${value}`;
  if (!warned.has(k)) {
    warned.add(k);
    console.warn('[gp:chip] 未映射状态值', column, value);
  }
  return chip('gray', value);
}

// 本地表单错误码：不属于 02 §13，errorText 对未登记码回落 message（TD-839），用于表单红条显示页面定制文案。
// 契约锁断言该码不在 USER_ERRORS / AGENT_ERRORS / ADMIN_ERRORS 三张表中；后端启用该码前必须先改此常量
export const LOCAL_FORM_CODE = 9000;

export function localErr(route: RouteKey, message: string): ApiError {
  return new ApiError({ code: LOCAL_FORM_CODE, status: 0, message, data: {}, route });
}
